import { Component, ViewChild, OnInit, signal, EventEmitter, Output, Input, OnChanges, effect, computed, NO_ERRORS_SCHEMA } from '@angular/core';
import { Router } from '@angular/router';
import { Utils } from '../../../shared/utils';
import { FlowDefinition } from '../../../models/flow.model';
import { FlowDefinitionService } from '../../../services/flow-definition.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../shared/enums';
import { CookieService } from 'ngx-cookie-service';
import { ConfirmationService } from 'primeng/api';
import { PopoverComponent } from '../../../components/popover/popover.component';
import { FlowObjectDefinitionService } from '../../../services/flow-object-definition.service';
import { FlowRules } from '../../../shared/flow-rules';
import { AuditLog } from '../../../models/auditlog.model';
import { ReloadService } from '../../../services/reload.service';
import { IMPORTS } from './_imports';
import { FlowDefinitionComponent } from '../../../pages/flow-definition/flow-definition.component';

@Component({
    selector: 'canvas-header',
    templateUrl: './canvas-header.component.html',
    styleUrl: './canvas-header.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class CanvasHeaderComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('actionsMatPopoverRef') actionsMatPopoverRef: PopoverComponent;
    @ViewChild('viewHistoryMatPopoverRef') viewHistoryMatPopoverRef: PopoverComponent;
    // #endregion

    // #region [Type props]
    Enums = Enums;
    Utils = Utils;
    // #endregion

    // #region [signals]
    $flowDefinition = signal(null as FlowDefinition);
    $isReadOnlyMode = signal(false);
    $isCreate = signal(false);
    $hasTemporalHotConfigs = signal(false);
    $changeVersionTrigger = signal(false);
    $selectedVersion = signal(null as number);
    $selectedFlowObjectsLength = signal(0);
    $historyEntries = signal([] as AuditLog[]);
    $auditAuthors = signal([]);
    // #endregion

    // #region [computeds]
    $flowDefinitionVersionOptions = computed(() => Array.from({ length: this.$flowDefinition().version }, (item, idx) => idx + 1).reverse());
    $flowInboxInfo = computed(() => this.$flowDefinition()?.unitInfo == null
        ? Enums.Messages.MyFlowDefinitions
        : '{0} - {1} - {2}'
            .replace('{0}', this.$flowDefinition()?.patriarchInfo?.split(' - ')[0] || '')
            .replace('{1}', this.$flowDefinition()?.organizationInfo?.split(' - ')[0] || '')
            .replace('{2}', this.$flowDefinition()?.unitInfo?.split(' - ')[0] || '')
    );
    // #endregion

    // #region [effects]
    $changeVersionTriggerEvent = effect(() => {
        if (this.$changeVersionTrigger()) {
            this.changeVersion();
            this.$changeVersionTrigger.set(false);
            this._changeVersionTriggerResetEvent.emit();
        }
    }, { allowSignalWrites: true });
    // #endregion

    // #region [Input/Output]
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Input() _isCreate: boolean;
    @Input() _hasTemporalHotConfigs: boolean;
    @Input() _changeVersionTrigger: boolean;
    @Input() _selectedVersion: number;
    @Input() _selectedFlowObjectsLength: number;
    @Output() _selectedVersionChange = new EventEmitter<number>();
    @Output() _saveFlowDefinitionEvent = new EventEmitter<any>();
    @Output() _openModalAreaTriggerEvent = new EventEmitter<any>();
    @Output() _changeVersionTriggerResetEvent = new EventEmitter<any>();
    // #endregion

    constructor(
        private router: Router,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private confirmationService: ConfirmationService,
        private cookieService: CookieService,
        private flowObjectDefinitionService: FlowObjectDefinitionService,
        private flowDefinitionService: FlowDefinitionService,
        private reloadService: ReloadService,
        private parent: FlowDefinitionComponent
    ) { }

    ngOnInit() { }

    ngOnChanges() {
        this.$flowDefinition.set(this._flowDefinition);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
        this.$isCreate.set(this._isCreate);
        this.$hasTemporalHotConfigs.set(this._hasTemporalHotConfigs);
        this.$changeVersionTrigger.set(this._changeVersionTrigger);
        this.$selectedVersion.set(this._selectedVersion);
        this.$selectedFlowObjectsLength.set(this._selectedFlowObjectsLength);
    }

    saveFlowDefinition() {
        this._saveFlowDefinitionEvent.emit();
    }

    editFlowDefinition() {
        this._openModalAreaTriggerEvent.emit();
    }

    reload() {
        this.reloadPage();
    }

    changeVersion(event?) {
        if (event != null) {
            this.$selectedVersion.set(event);
            this._selectedVersionChange.emit(this.$selectedVersion());
        }

        this.router.navigate([
            Enums.PagesPaths.FlowDefinition,
            this.$flowDefinition().id,
            (this.$selectedVersion() == this.$flowDefinition().version ? '' : this.$selectedVersion())
        ]);
    }

    async publish() {
        if (!(await FlowRules.isFlowDefinitionValid(this.$flowDefinition(), this.$isReadOnlyMode(), this.toastr, this.flowObjectDefinitionService))) {
            return;
        }

        this.executeAction(
            this.publish.name,
            Enums.Messages.PublishConfirmMessage,
            () => this.reloadPage()
        );
    }

    newVersion() {
        this.executeAction(
            this.newVersion.name,
            Enums.Messages.NewVersionConfirmMessage,
            () => this.reloadPage()
        );
    }

    clone() {
        this.executeAction(
            this.clone.name,
            Enums.Messages.CloneConfirmMessage,
            (clonedFlowDefinition: FlowDefinition) => this.reloadPage(clonedFlowDefinition.id)
        );
    }

    activate() {
        this.executeAction(
            this.activate.name,
            Enums.Messages.ActivateConfirmMessage,
            () => this.reloadPage()
        );
    }

    deactivate() {
        this.executeAction(
            this.deactivate.name,
            Enums.Messages.DeactivateConfirmMessage,
            () => this.reloadPage()
        );
    }

    delete() {
        this.executeAction(
            this.delete.name,
            Enums.Messages.DeleteConfirmMessage,
            () => this.router.navigate([Enums.PagesPaths.FlowDefinitionList])
        );
    }

    instantiate() {
        let environment = atob(this.cookieService.get('prodest-eflow-env'));
        if (environment.toLowerCase() == 'loc') {
            open(`${location.protocol}//${location.hostname}:44222${Enums.PagesPaths.FlowDefinition}/${this.$flowDefinition().id}`);
        } else {
            open(`${location.origin.split('admin.').join('')}${Enums.PagesPaths.FlowDefinition}/${this.$flowDefinition().id}`);
        }

        this.actionsMatPopoverRef.close();
    }

    async loadHistory() {
        this.spinner.show('flow');

        const response = await this.flowDefinitionService.getHistory({ key: this.$flowDefinition().id });

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        Utils.setSignal(this.$historyEntries, response.data);
        if (this.$historyEntries().length > 0) {
            let currentEntryUser = this.$historyEntries()[0].auditUserName;

            for (let i = 0; i < this.$historyEntries().length; i++) {
                let auditDate = this.$historyEntries()[i].auditDate;
                let finalSuffix = '';
                let now = new Date() as any;
                let then = new Date(auditDate) as any;
                let diff = (now - then)/1000;

                if (diff < 60) {
                    finalSuffix += Math.floor(diff) + ' segundo' + (Math.floor(diff) > 1 ? 's' : '');
                } else if (diff/60 < 60) {
                    finalSuffix += Math.floor(diff/60) + ' minuto' + (Math.floor(diff/60) > 1 ? 's' : '');
                } else if (diff/60/60 < 24) {
                    finalSuffix += Math.floor(diff/60/60) + ' hora' + (Math.floor(diff/60/60) > 1 ? 's': '');
                } else if (diff/60/60/24 < 30) {
                    finalSuffix += Math.floor(diff/60/60/24) + ' dia' + (Math.floor(diff/60/60/24) > 1 ? 's' : '');
                } else if (diff/60/60/24/30 < 12) {
                    finalSuffix += Math.floor(diff/60/60/24/30) + (Math.floor(diff/60/60/24/30) > 1 ? ' meses' : ' mês');
                } else {
                    finalSuffix += Math.floor(diff/60/60/24/30/12) + ' ano' + (Math.floor(diff/60/60/24/30/12) > 1 ? 's' : '');
                }

                Utils.updSignal(this.$historyEntries, x => {
                    x[i].auditDate = this.getAuditDateString(x[i].auditDate) + `*${finalSuffix} atrás`;
                    return x;
                });

                if (i > 0 && this.$historyEntries()[i].auditUserName == currentEntryUser) {
                    Utils.updSignal(this.$historyEntries, x => {
                        x[i].auditUserName = [x[i].auditUserName, ''].join('*');
                        return x;
                    });
                } else {
                    currentEntryUser = this.$historyEntries()[i].auditUserName;
                }
            }
        }

        Utils.setSignal(this.$auditAuthors, [...new Set(this.$historyEntries().map(x => (Utils.isNotNullOrEmpty(x.auditUserName) ? x.auditUserName : Enums.Messages.DefaultEmptyValue).replace('*', '')))]);

        this.viewHistoryMatPopoverRef.open();

        this.spinner.hide('flow');
    }

    // #region [===== private methods =====]
    // #endregion

    private executeAction(action: string, confirmationMessage: string, callbackAfterSuccess: Function) {
        this.actionsMatPopoverRef.close();

        this.confirmationService.confirm({
            message: confirmationMessage.replace('{0}', this.$flowDefinition().name),
            accept: async () => {
                this.spinner.show('flow');

                const response = await this.flowDefinitionService[action](this.$flowDefinition().id);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    this.spinner.hide('flow');
                    return;
                }

                this.toastr.success(response.message.description, Enums.Messages.Success);
                callbackAfterSuccess(response.data);
                this.spinner.hide('flow');
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    private reloadPage(flowDefinitionId?: string) {
        if (flowDefinitionId != null) {
            this.router.navigate([Enums.PagesPaths.FlowDefinition, flowDefinitionId]);
        }

        this.parent.ngOnDestroy();
        setTimeout(() => this.reloadService.trigger(), 100);
    }

    private getAuditDateString(date: string) {
        return new Date(date).toLocaleString();
    }
}
